import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSmartphone, FiDatabase, FiCloud, FiCpu, FiCheckCircle, FiArrowRight, FiTrendingUp } = FiIcons;

const DigitalTransformation = () => {
  const technologies = [
    {
      icon: FiDatabase,
      title: 'ERP Implementations',
      description: 'Comprehensive enterprise resource planning systems tailored for South African business requirements and regulatory compliance.'
    },
    {
      icon: FiTrendingUp,
      title: 'Data Analytics Platforms',
      description: 'Advanced analytics solutions that provide actionable insights for data-driven decision making in local market conditions.'
    },
    {
      icon: FiCloud,
      title: 'Customer Relationship Management',
      description: 'CRM systems optimized for South African customer engagement patterns and multi-cultural communication preferences.'
    },
    {
      icon: FiCpu,
      title: 'Artificial Intelligence Applications',
      description: 'AI-powered solutions addressing specific South African business challenges, from resource optimization to customer service.'
    }
  ];

  const infrastructureChallenges = [
    'Load shedding and power reliability',
    'Internet connectivity variations',
    'Mobile-first user preferences',
    'Multi-language support requirements',
    'Legacy system integration',
    'Skills development and training',
    'Regulatory compliance automation',
    'Cost-effective cloud migration'
  ];

  const mobileOpportunities = [
    'Mobile payment integration',
    'WhatsApp Business automation',
    'Progressive web applications',
    'Offline-first functionality',
    'Location-based services',
    'Voice and SMS integration',
    'Mobile workforce management',
    'Digital wallet connectivity'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-serif">
              Digital Transformation & Technology Integration
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Recognising South Africa's rapid digital adoption, particularly post-2020, we guide organisations through comprehensive technology transformation programmes. Our expertise spans ERP implementations, data analytics platforms, customer relationship management systems, and emerging technologies including artificial intelligence applications.
            </p>
            <Link
              to="/contact"
              className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center"
            >
              Start Your Digital Journey
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Technology Solutions */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Technology Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We prioritise solutions that address South Africa's unique infrastructure challenges whilst maximising opportunities presented by mobile-first consumer behaviour and growing fintech ecosystem.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={tech.icon} className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {tech.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Opportunities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Addressing Unique South African Context
            </h2>
            <p className="text-xl text-gray-600">
              Our approach recognises the specific infrastructure challenges and unique opportunities in the South African digital landscape.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Infrastructure Challenges */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiCloud} className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">
                Infrastructure Challenges
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                We design solutions that work reliably within South Africa's infrastructure constraints, ensuring business continuity and optimal performance.
              </p>
              <ul className="space-y-3">
                {infrastructureChallenges.map((challenge, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    {challenge}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Mobile Opportunities */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiSmartphone} className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">
                Mobile-First Opportunities
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Leverage South Africa's high mobile adoption rates and innovative fintech ecosystem to create competitive advantages and reach new markets.
              </p>
              <ul className="space-y-3">
                {mobileOpportunities.map((opportunity, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {opportunity}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transformation Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Digital Transformation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A phased approach that minimises disruption while maximising the benefits of digital transformation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                phase: 'Assessment',
                title: 'Digital Readiness Audit',
                description: 'Comprehensive evaluation of current systems, infrastructure, and digital capabilities.',
                duration: '2-3 weeks'
              },
              {
                phase: 'Strategy',
                title: 'Transformation Roadmap',
                description: 'Development of phased implementation plan with clear priorities and success metrics.',
                duration: '3-4 weeks'
              },
              {
                phase: 'Implementation',
                title: 'Phased Deployment',
                description: 'Structured rollout with change management, training, and continuous support.',
                duration: '3-12 months'
              },
              {
                phase: 'Optimization',
                title: 'Performance Enhancement',
                description: 'Ongoing optimization, user adoption monitoring, and continuous improvement.',
                duration: 'Ongoing'
              }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-lg font-bold">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-purple-700 mb-2">{phase.phase}</h3>
                <h4 className="text-xl font-bold text-gray-900 mb-4">{phase.title}</h4>
                <p className="text-gray-600 mb-4 leading-relaxed">{phase.description}</p>
                <span className="text-sm text-gray-500 font-medium">{phase.duration}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
                Measurable Digital Impact
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our digital transformation initiatives deliver quantifiable results that drive business value and competitive advantage in the South African market.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2">65%</div>
                  <div className="text-gray-600">Average efficiency improvement</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2">40%</div>
                  <div className="text-gray-600">Reduction in operational costs</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2">85%</div>
                  <div className="text-gray-600">User adoption rate</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2">12 months</div>
                  <div className="text-gray-600">Average ROI timeline</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Digital transformation dashboard"
                className="rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-serif">
              Ready to Accelerate Your Digital Transformation?
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Let's discuss how digital technologies can drive efficiency, growth, and competitive advantage for your South African business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center justify-center"
              >
                Schedule Digital Assessment
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-purple-900 transition-colors inline-flex items-center justify-center"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DigitalTransformation;